<template>
    <div ref="profile-editor" aria-label="profile editor" role="group" id="profile-editor" v-if="customer" class="columns is-centered">
        <div class="column is-9-desktop is-12-mobile is-10-tablet">
            <horizontal-input>
                <template slot="label">Name</template>
                <input
                    placeholder="Full Name"
                    slot="body"
                    class="input is-hovered" 
                    type="text"
					aria-label="Enter your name"
                    v-model="name" >
            </horizontal-input>
            <hr>
            <horizontal-input>
                <template slot="label">Mobile Number</template>
                <div class="is-relative" slot="body">
                    <BaseIcon
                    class="input-check"
                    icon="checkCircleLight"
                    size="1x"
                    v-if="liveValidate"/>
                <input
                    :class="{'text-indent': liveValidate}"
                    class="input is-hovered" 
                    type="tel"
					aria-label="Enter your mobile number"
                    v-model="phone" />
                </div>
            </horizontal-input>
            <hr>
            <horizontal-input v-if="showEmailInput">
                <template slot="label">Email</template>
                <div class="is-relative" slot="body">
                    <BaseIcon
                    class="input-check"
                    icon="checkCircleLight"
                    size="1x"
                    v-if="isValidEmail"/>
                    <input
                        :class="{'text-indent': isValidEmail}"
                        class="input is-hovered" 
                        type="email" 
						aria-label="Enter your email"
                        v-model="email" />
                </div>
            </horizontal-input>
			<hr v-if="showEmailInput">
			<horizontal-input v-if="showMessagingInput">
				<template slot="label">Messaging</template>
					<div slot="body" class="flex-column control-group">
						<template v-if="isValidEmail">
							<div class="flex-full-width">
								<label class="control control--checkbox cust-radio">
									<input type="checkbox"
										role="checkbox"
										:aria-checked="profileChanges.receive_offers"
										aria-label="receive email offers"
										@keydown.enter="toggleEmailOffers()"
                                        @click="toggleEmailOffers()" 
                                        v-model = "profileChanges.receive_offers"
                                        />
                                    <div class="control__indicator checkbox"></div>
                                <span>Receive Email Offers</span>
                                </label>
                            </div>
                        </template>
                        <p v-else> Please enter an email</p>
                    </div>
            </horizontal-input>
            <hr v-if="showMessagingInput">
            <horizontal-input v-if="$deliveryEnabled">
                <template slot="label">Address</template>
                <template slot="body">
                    <BaseMap
                        v-if="customer.addresses.length && deliveryAddress"
                        :storeLocation=deliveryAddress
                    />
                    <AddressChooser/>
                </template>
            </horizontal-input>
            <hr v-if="$deliveryEnabled">
            <horizontal-input>
                <template slot="label">Payment</template>
                    <PaymentChooser slot="body"/>
            </horizontal-input>
                <p>{{msg}}</p>
                <div class="has-text-centered button-container">
                    <BaseButton title="Save"
                    :classNames="['button--modifier']"
                    @click.native="submitProfile" />
                </div>
				<div class="has-text-centered button-container">
					<BaseButton title="Sign-out"
					:classNames="['button--modifier']"
					@click.native="handleSignOutAction" />
				</div>
        </div>
        <v-dialog/>
    </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { ACCOUNT, ACCOUNT_CHECKOUT, LOCATION } from "@/stores/defs";
import HorizontalInput from "@/components/form/HorizontalInput";
import AddressChooser from "@/components/checkout/AddressChooser";
import PaymentChooser from "@/components/checkout/PaymentChooser";
import customerService from "@/services/customer.service";
import { splitName, hasNewCustomerName } from "@/utils/helper.functions";
import  FormValidator from "@/utils/FormValidator";
import phoneValidatorMixin from '@/components/mixins/phoneValidatorMixin';
import { ERROR_MSG } from '@/common/messages/error.msg';
import alertMixin from '@Mixin/alertMixin';

export default {
    mixins: [
		phoneValidatorMixin,
		alertMixin,
	],
    computed: {
        ...mapGetters({
            customer: ACCOUNT.GET_CUSTOMER,
            deliveryAddress: ACCOUNT_CHECKOUT.GET_DELIVERY_ADDRESS,
            location: LOCATION.GET_LOCATION
        }),
        profileChanges: {
            get() {
                const fullName = splitName(this.name);
                return {
                    firstName: fullName.firstName || "",
                    lastName: fullName.lastName || "",
                    email: this.email || "",
                    receive_offers: this.customer.receiveOffers,
                    phone: this.phone || ""
                };
            }
        },
        isValidEmail: {
            get() {
                return FormValidator.validateEmail(this.email);
            }
        }
    },
    data() {
        return {
            email: "",
            msg: "",
			name: "",
			showEmailInput: false,
			showMessagingInput: false,
        };
    },
    components: {
        "horizontal-input": HorizontalInput,
        AddressChooser,
        PaymentChooser
    },
    mounted() {
        if (this.customer.firstName) {
            this.name = `${this.customer.firstName}`
        }

        if (this.customer.lastName) {
            this.name += ` ${this.customer.lastName}`;
        }

        if (this.customer.email) {
            this.email = this.customer.email;
		}
    },
    methods: {
        ...mapActions({
            updateCustomer: ACCOUNT.UPDATE_CUSTOMER
		}),
		handleSignOutAction() {
			const title = 'Sign-out';
			const message = 'Are you sure you want to sign-out?';
			const logoutHandler = () => { this.logoutClick(); }
			const cancelHandler = () => { this.$modal.hide('dialog'); }
			const buttons = [
				{
					title: 'Yes',
					handler: logoutHandler,
				},
				{
					title: 'No',
					handler: cancelHandler,
				}
			];
			this.customAlert(title, message, ...buttons);
		},
		toggleEmailOffers() {
			const receiveOffers = this.profileChanges.receive_offers;
			this.profileChanges.receive_offers = !receiveOffers;
		},
		checkEmailForChanges() {
			const useEmail = +this.$appConstants.account.profileShowEmail;
			if (!useEmail) {
				return;
			}
			const emailChanges = this.profileChanges.email;
			const validEmail = FormValidator.validateEmail(emailChanges);
			if (!validEmail || !emailChanges) {
				this.msg = ERROR_MSG.PROFILE.MISSING_EMAIL;
				return;
			}
		},
        submitProfile() {
            this.msg = "";
            let apiReq = {
                lb: this.location.id,
                c: {
                    base: this.customer.id,
                    phone: this.customer.phone
                }
            };
            if (!this.profileChanges.firstName || hasNewCustomerName(this.profileChanges.firstName, this.profileChanges.lastName)) {
                this.msg = "Please enter a valid name";
            } 

			this.checkEmailForChanges();

            const strippedPhone = this.profileChanges.phone.replace(/\D/g,'');
            if (this.profileChanges.phone && !this.customer.phone.includes(strippedPhone)) {
                apiReq.newPhone = this.profileChanges.phone;
            } else if (!this.liveValidate) {
                this.msg = ERROR_MSG.PROFILE.MISSING_PHONE;
			}
			const vue = this;
            if (!this.msg) {
                // res.data.message === 'Customer save!'
                for (let key in this.profileChanges) {
                    if ( (this.profileChanges[key] || typeof this.profileChanges[key] === "boolean" || this.profileChanges[key] === "")  && this.profileChanges[key] !== this.customer[key] ) {
                        apiReq.c[key.toLowerCase()] = this.profileChanges[key];
                    }
                }
                customerService
                    .editProfile(apiReq)
                    .then(res => {
                        if (res.data.message === "Customer save!") {
                            this.msg = "Your profile was successfully updated.";
                            this.updateCustomer({
                                firstName: res.data.body.customer.firstname,
                                lastName: res.data.body.customer.lastname,
                                email: res.data.body.customer.email,
                                receiveOffers: res.data.body.customer.receive_offers,
                                phone: res.data.body.customer.phone
							});
							// TODO put in mixin
							const customer = this.customer;
							this.$tpa.trackUserProfileUpdate(customer);

                            setTimeout(() => {
                                this.msg = "";
								vue.$modal.hide('Profile');
                            }, 1000);
                        } else if (res.data.body.errors) {
                            this.msg = res.data.body.errors[0];
                        }
                    })
                    .catch(err => {
                        this.msg = err.message;
                    });
            }
        },
        logoutClick() {
            this.$router.push({ name: 'logout' });
        }
    },
    watch: {
        "profileChanges.email": {
            handler(newValue, oldValue) {
                if (newValue.email) {
                    this.isValidEmail = FormValidator.validateEmail(this.profileChanges.email);
                }
            }, 
            deep: true
        }
	},
	created() {
		const appConstants = this.$appConstants;
		const accountSettings = appConstants.account;
		const showEmail = +accountSettings.profileShowEmail;
		const showMessaging = +accountSettings.profileShowMessaging;
		this.showEmailInput = !!showEmail;
		this.showMessagingInput = !!showMessaging;
	}
};
</script>

<style lang="scss">
@import "globalStyles";

.text-indent {
    text-indent: 1.1em;
}

.profile-sign-out {
    position: absolute;
    bottom: 1em;
    right: 1em;
}

.button-container {
    margin-bottom: 1em;
}

#profile-editor {
    width: 100%;
    padding-top: 1em;
    margin: auto;
}

@include mobile {
    #profile-editor {
        padding-bottom: 20vh;
    }
    .profile-sign-out {
        bottom: 10em;
    }
}
</style>



// WEBPACK FOOTER //
// src/components/account/ProfileEditor.vue